import { useState, useEffect } from 'react';
import { Modal, Image, ActionIcon, Group, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react';

interface ImageLightboxProps {
  images: string[];
  initialIndex: number;
  opened: boolean;
  onClose: () => void;
}

export function ImageLightbox({ images, initialIndex, opened, onClose }: ImageLightboxProps) {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex, opened]);

  useEffect(() => {
    if (!opened) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setIndex(i => (i - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setIndex(i => (i + 1) % images.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [opened, images.length]);

  const prev = () => setIndex(i => (i - 1 + images.length) % images.length);
  const next = () => setIndex(i => (i + 1) % images.length);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      padding={0}
      withCloseButton={false}
      styles={{
        body: { padding: 0 },
        content: { background: 'rgba(0,0,0,0.92)', borderRadius: 'var(--mantine-radius-lg)' },
        overlay: { backdropFilter: 'blur(4px)' },
      }}
      centered
    >
      {/* Close button */}
      <ActionIcon
        onClick={onClose}
        variant="subtle"
        color="gray"
        size="lg"
        style={{ position: 'absolute', top: 12, right: 12, zIndex: 10, color: 'white' }}
      >
        <IconX size={20} />
      </ActionIcon>

      {/* Image */}
      <Image
        src={images[index]}
        alt={`Image ${index + 1} of ${images.length}`}
        fit="contain"
        mah="80vh"
        w="100%"
        style={{ display: 'block' }}
      />

      {/* Navigation */}
      {images.length > 1 && (
        <Group justify="space-between" align="center" p="sm" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <ActionIcon onClick={prev} variant="subtle" color="gray" size="lg" style={{ color: 'white' }}>
            <IconChevronLeft size={22} />
          </ActionIcon>
          <Text size="sm" c="dimmed">
            {index + 1} / {images.length}
          </Text>
          <ActionIcon onClick={next} variant="subtle" color="gray" size="lg" style={{ color: 'white' }}>
            <IconChevronRight size={22} />
          </ActionIcon>
        </Group>
      )}
    </Modal>
  );
}
